import HTTP from '@/common/HTTP'

import Vue from 'vue';
import { merge, indexOf, cloneDeep } from 'lodash';
import { actionTypes, mutationTypes, getTypes } from './DraftsTypes';
import { actionTypes as globalActionTypes } from './GlobalTypes'

const initialState = () => ({
  surveyId: null,
  draft: null,
  editQuestion: null,
  editGroup: null,
})

const questionPrototype = {
  id: null,
  title: '',
  description: '',
  type: 'FREETEXT',
  knockOutQuestion: false,
  maxSelect: 1,
  preset: '',
  cps: 0,
  availableOptions: []
};

const groupPrototype = {
  id: null,
  title: '',
  parent: null,
  description: '',
  position: 0,
  weight: 1,
  dependentOption: null,
}

const getters = {
  [getTypes.HIGHLIGHTED_GROUPS](state) {
    if (state.editGroup !== null) {
      return [state.editGroup.id]
    }
    if (state.editQuestion !== null) {
      return [state.editQuestion.group]
    }
    return []
  },

  [getTypes.HIGHLIGHTED_QUESTION](state) {
    if (state.editQuestion !== null) {
      return [state.editQuestion.id]
    }
    return []
  },

  [getTypes.EDIT_ACTIVE](state) {
    return state.editGroup !== null || state.editQuestion !== null
  },
}

const mutations = {

  // groups

  [mutationTypes.SET_EDIT_GROUP](state, group) {
    state.editQuestion = null
    state.editGroup = cloneDeep(group)
  },

  [mutationTypes.UPDATE_EDIT_GROUP](state, group) {
    merge(state.editGroup, group)
  },

  [mutationTypes.ADD_GROUP](state, parentGroup) {
    const group = cloneDeep(groupPrototype)
    if (parentGroup !== null) {
      group.parent = parentGroup.id
    }
    state.editGroup = group
  },

  [mutationTypes.REMOVE_GROUP](state, group) {
    Vue.delete(state.draft.questionGroups, indexOf(state.draft.questionGroups, group))
  },

  // questions

  [mutationTypes.SET_DRAFT](state, { surveyId, draft }) {
    state.draft = draft
    state.surveyId = surveyId
  },

  [mutationTypes.SET_EDIT_QUESTION](state, question) {
    state.editGroup = null
    state.editQuestion = cloneDeep(question)
  },

  [mutationTypes.UPDATE_EDIT_QUESTION](state, question) {
    merge(state.editQuestion, question)
    if (question.type === 'MULTI_SELECT') {
      state.editQuestion.availableOptions = cloneDeep(question.availableOptions)
    }
  },

  [mutationTypes.ADD_QUESTION](state, group) {
    const question = cloneDeep(questionPrototype)
    question.group = group.id
    state.editQuestion = question
  },
}

const actions = {
  async [actionTypes.FETCH_DRAFT]({ commit }, surveyId) {
    const { data } = await HTTP.get(`/surveys/${surveyId}/draft`)
    commit(mutationTypes.SET_DRAFT, {
      surveyId,
      draft: data,
    })
    commit(mutationTypes.SET_EDIT_GROUP, null)
    commit(mutationTypes.SET_EDIT_QUESTION, null)
    return data
  },

  // Groups

  async [actionTypes.SAVE_GROUP]({ commit, dispatch, state }) {
    const questionGroup = state.editGroup
    let data;
    if (questionGroup.id === null) {
      data = (await HTTP.post(`/surveys/${state.surveyId}/draft/question-groups`, questionGroup)).data
    } else {
      data = (await HTTP.patch(`/question-groups/${questionGroup.id}`, questionGroup)).data
    }

    dispatch(actionTypes.FETCH_DRAFT, state.surveyId)
    dispatch(globalActionTypes.SHOW_SNACKBAR, { message: 'Änderungen wurden erfolgreich gespeichert' }, { root: true })

    commit(mutationTypes.SET_EDIT_GROUP, null)

    return data
  },

  async [actionTypes.MOVE_GROUP_UP]({ commit, state, dispatch }, questionGroup) {
    await HTTP.patch(`/question-groups/${questionGroup.id}/move-up`)
    dispatch(actionTypes.FETCH_DRAFT, state.surveyId)
  },

  async [actionTypes.MOVE_GROUP_DOWN]({ commit, state, dispatch }, questionGroup) {
    await HTTP.patch(`/question-groups/${questionGroup.id}/move-down`)
    dispatch(actionTypes.FETCH_DRAFT, state.surveyId)
  },

  async [actionTypes.DELETE_GROUP]({ commit, dispatch, state }, questionGroup) {
    await HTTP.delete(`/question-groups/${questionGroup.id}`)
    dispatch(actionTypes.FETCH_DRAFT, state.surveyId)
    dispatch(globalActionTypes.SHOW_SNACKBAR, { message: 'Gruppe wurde gelöscht' }, { root: true })
  },

  // Questions

  async [actionTypes.SAVE_QUESTION]({ dispatch, commit, state }) {
    const question = state.editQuestion
    if (question.availableOptions && question.type === 'MULTI_SELECT') {
      question.options = question.availableOptions
      delete question.availableOptions
    }
    let data;
    if (question.id === null) {
      data = (await HTTP.post(
        `/surveys/${state.surveyId}/draft/question-groups/${question.group}/questions`, question
      )).data
    } else {
      data = (await HTTP.patch(`/questions/${question.id}`, question)).data
    }

    commit(mutationTypes.SET_EDIT_QUESTION, null)

    dispatch(actionTypes.FETCH_DRAFT, state.surveyId)
    return data
  },

  async [actionTypes.MOVE_QUESTION_UP]({ commit, state, dispatch, rootState }, question) {
    await HTTP.patch(`/questions/${question.id}/move-up`)
    dispatch(actionTypes.FETCH_DRAFT, state.surveyId)
  },

  async [actionTypes.MOVE_QUESTION_DOWN]({ state, dispatch }, question) {
    await HTTP.patch(`/questions/${question.id}/move-down`)
    dispatch(actionTypes.FETCH_DRAFT, state.surveyId)
  },

  async [actionTypes.DELETE_QUESTION]({ commit, dispatch, state }, question) {
    try {
      await HTTP.delete(`/questions/${question.id}`)
    } catch (error) {
      dispatch(globalActionTypes.SHOW_SNACKBAR, {
        message: 'Frage verweist auf eine Gruppe und konnte daher nicht gelöscht werden',
        color: 'error'
      }, { root: true })
    }

    dispatch(actionTypes.FETCH_DRAFT, state.surveyId)
  },
}

export default {
  namespaced: true,
  state: initialState,
  actions,
  mutations,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/Drafts.js